import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { ChevronDownIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";

import useCustomerDetails from "../../data/useCustomerDetails";
import usePartsLeadLookup from "../../data/usePartsLeadLookup";
import { useFeatureEnabled } from "../../hooks/useFeatureFlag";
import { IVehicleEquipment } from "../../models/PartLookup";
import { IVehicle } from "../../models/Vehicle";
import useNotification from "../notifications/useNotifications";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";

interface IEquipmentEntry {
  title: string;
  code: string;
  rows: { label: string; value: string }[];
}

interface IEquipmentGroup {
  family: string;
  entries: IEquipmentEntry[];
}

const clean = (value: string | null | undefined) => value?.trim() ?? "";

// GT Motive repeats itself a lot - sub-family, description and value are often
// the same string. Only keep a row if it adds something not already shown.
function buildRows(item: IVehicleEquipment, title: string, labels: Record<string, string>) {
  const rows: { label: string; value: string }[] = [];
  const seen = new Set([title.toLowerCase()]);

  const add = (label: string, value: string, allowDuplicate = false) => {
    if (!value) return;
    if (!allowDuplicate && seen.has(value.toLowerCase())) return;
    seen.add(value.toLowerCase());
    rows.push({ label, value });
  };

  add(labels.description, clean(item.description));
  add(labels.value, clean(item.value));
  add(labels.manufacturerCodes, clean(item.manufacturerCodes), true);
  add(labels.source, clean(item.source), true);

  return rows;
}

// Equipment comes back as a flat list; group it by family so a 50+ item list
// reads as sections rather than a wall of rows.
function groupEquipment(
  equipment: IVehicleEquipment[],
  other: string,
  labels: Record<string, string>
): IEquipmentGroup[] {
  const groups: IEquipmentGroup[] = [];

  equipment.forEach((item) => {
    const familyName = clean(item.familyDescription) || clean(item.family) || other;
    const title =
      clean(item.subFamilyDescription) || clean(item.subFamily) || clean(item.description) || other;

    let group = groups.find((g) => g.family === familyName);
    if (!group) {
      group = { family: familyName, entries: [] };
      groups.push(group);
    }

    group.entries.push({
      title,
      code: [clean(item.family), clean(item.subFamily)].filter(Boolean).join(" / "),
      rows: buildRows(item, title, labels),
    });
  });

  return groups;
}

export default function VehiclePartProDetails({ vehicle }: { vehicle: IVehicle }) {
  const { t } = useTranslation("vehicles");
  const { brand } = useCustomerDetails();
  const { addNotification } = useNotification();
  const partsLookupEnabled = useFeatureEnabled("Parts Lookup");

  const [isOpen, setIsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hasLookup = vehicle.partsLookupRequestId != null;

  const { lookup, lookupMutation } = usePartsLeadLookup(
    undefined,
    vehicle.id,
    vehicle.partsLookupRequestId,
    "VehiclePartProDetails",
    isOpen // only fetch the cached result once the section is opened
  );

  const equipment = lookup.data?.data?.vehicleEquipment ?? [];
  const groups = useMemo(
    () =>
      groupEquipment(equipment, t("Other"), {
        description: t("Description"),
        value: t("Value"),
        manufacturerCodes: t("Manufacturer Codes"),
        source: t("Source"),
      }),
    [equipment, t]
  );

  if (!partsLookupEnabled) return null;

  const handleLookup = () => {
    lookupMutation.mutate(false, {
      onSuccess: (data) => {
        setConfirmOpen(false);

        if (!data.data?.vehicleEquipment || data.data.vehicleEquipment.length === 0) {
          addNotification({ primaryText: t("No vehicle details found"), variant: "info" });
          return;
        }

        addNotification({ primaryText: t("PartPro Lookup Successful"), variant: "success" });
      },
      onError: (error) => {
        console.error("PartPro lookup failed:", error);
        setConfirmOpen(false);
        addNotification({ primaryText: t("PartPro Lookup Failed"), variant: "error" });
      },
    });
  };

  const isLoading = lookup.isFetching || lookupMutation.isLoading;

  return (
    <div className="border-t border-gray-200 py-4">
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={handleLookup}
        isLoading={lookupMutation.isLoading}
        title={t("Activate PartPro Lookup?")}
        message={t(
          "This will start a PartPro lookup on this vehicle, this may take some time.\n\nThis costs 1 token per lookup per vehicle (multiple parts from the same vehicle count as one lookup), you have {{tokens}} tokens remaining.",
          { tokens: brand.data?.data.lookupTokens ?? 0 }
        )}
        confirmButtonText={null}
      />

      <div
        className="flex items-center justify-between cursor-pointer select-none mb-2"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <WrenchScrewdriverIcon className="h-5 w-5 text-primary-600" />
          <h3 className="text-sm font-medium text-gray-900">
            {equipment.length > 0
              ? t("PartPro Vehicle Details ({{count}})", { count: equipment.length })
              : t("PartPro Vehicle Details")}
          </h3>
        </div>
        <ChevronDownIcon
          className={`h-5 w-5 text-gray-400 transition-transform duration-200 ease-in-out ${
            isOpen ? "" : "rotate-180"
          }`}
        />
      </div>

      {isOpen && (
        <>
          {isLoading ? (
            <div className="py-6">
              <LoadingWheel />
            </div>
          ) : !hasLookup && equipment.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-600">
                {t("A PartPro lookup has not been performed for this vehicle.")}
              </p>
              <ButtonInput
                label={t("Perform Lookup")}
                isSubmit={false}
                onClick={() => setConfirmOpen(true)}
                isLoading={false}
                classes="mt-3"
                size="sm"
              />
            </div>
          ) : lookup.isError ? (
            <p className="text-xs text-red-600">{t("Unable to load PartPro vehicle details.")}</p>
          ) : equipment.length === 0 ? (
            <p className="text-xs text-gray-500">
              {t("No vehicle equipment was returned for this vehicle.")}
            </p>
          ) : (
            <div className="space-y-4">
              {groups.map((group) => (
                <div key={group.family}>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                    {group.family}
                  </h4>
                  <div className="space-y-2">
                    {group.entries.map((entry, index) => (
                      <div
                        key={`${entry.title}-${index}`}
                        className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-xs font-medium text-gray-900">{entry.title}</span>
                          {entry.code && (
                            <span className="text-[10px] text-gray-400 whitespace-nowrap">
                              {entry.code}
                            </span>
                          )}
                        </div>
                        {entry.rows.length > 0 && (
                          <dl className="mt-1 space-y-0.5">
                            {entry.rows.map((row) => (
                              <div key={row.label} className="flex items-start gap-2">
                                <dt className="text-[11px] text-gray-500 min-w-[110px] flex-none">
                                  {row.label}
                                </dt>
                                <dd className="text-[11px] text-gray-800 break-words">
                                  {row.value}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
